import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, todayISO } from "../lib/api";
import { PageHeader } from "../components/PageHeader";

const COLS = [
  ["present", "Present", "text-emerald-600"],
  ["late", "Late", "text-amber-600"],
  ["absent", "Absent", "text-rose-600"],
  ["off_days", "Off days", "text-indigo-600"],
  ["holidays", "Holidays", "text-sky-600"],
  ["sick_leave", "Sick leave", "text-fuchsia-600"],
  ["worked_on_off", "Worked on off", "text-indigo-500"],
  ["late_minutes", "Late min", "text-amber-500"],
  ["hours", "Hours", "text-slate-900"],
];

export default function Summary() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/reports/monthly", { params: { month } }).then((r) => setRows(r.data.rows)).catch((e) => toast.error(errMsg(e)));
  }, [month]);

  const exportCsv = () => {
    const head = ["Code", "Name", "Department", "Weekly Off", ...COLS.map((c) => c[1])];
    const lines = [head, ...rows.map((r) => [r.code, r.name, r.department, r.weekly_off_day, ...COLS.map((c) => r[c[0]])])];
    const csv = lines.map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `summary_${month}.csv`; a.click();
  };

  return (
    <>
      <PageHeader overline="Per-worker totals" title="Monthly Summary">
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="summary-month-input" className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-slate-900" />
        <button onClick={exportCsv} data-testid="summary-csv-export-btn" className="h-10 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-colors">
          <Download size={16} /> Export CSV
        </button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="summary-table">
          <thead className="text-left text-xs uppercase text-slate-500 font-mono bg-slate-50">
            <tr><th className="px-5 py-3">Worker</th><th className="px-5 py-3">Weekly off</th>{COLS.map((c) => <th key={c[0]} className="px-5 py-3 text-right">{c[1]}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-400">No workers to summarize.</td></tr>}
            {rows.map((r) => (
              <tr key={r.worker_id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`summary-row-${r.worker_id}`}>
                <td className="px-5 py-3"><p className="font-medium">{r.name}</p><p className="font-mono text-xs text-slate-400">{r.code} · {r.department || "—"}</p></td>
                <td className="px-5 py-3 text-slate-600">{r.weekly_off_day}</td>
                {COLS.map((c) => <td key={c[0]} className={`px-5 py-3 text-right font-mono font-bold ${c[2]}`}>{r[c[0]]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
